import { useEffect, useRef, useCallback, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import XYZ from 'ol/source/XYZ';
import TileWMS from 'ol/source/TileWMS';
import { GeoTIFF as OLGeoTIFF } from 'ol/source';
import { fromLonLat, toLonLat } from 'ol/proj';
import DragBox from 'ol/interaction/DragBox';
import { platformModifierKeyOnly } from 'ol/events/condition';
import type { Source as TileSource } from 'ol/source/Tile';
import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { Style, Fill, Stroke, Circle } from 'ol/style';
import { encodeDigipin } from '../utils/digipinEncoder';
import { useAppSelector, useAppDispatch, useAppStore } from '../store/hooks';
import {
  setMapView,
  setMapExtent,
  setBasemapError,
  setCurrentBasemap,
} from '../store/slices/mapSlice';
import {
  setPendingGeometry,
  setDigitizationError,
  stopDigitization,
} from '../store/slices/digitizationSlice';
import {
  selectFeature,
  addFeatureToSelection,
  removeFromSelection,
  clearSelection,
  setSelection,
  selectSelectedFeatures,
  type SelectedFeature,
} from '../store/slices/selectionSlice';
import { getBasemapConfig, type BasemapType } from '../config/basemaps';
import type { Layer } from '../store/slices/layersSlice';
import {
  geojsonToVectorLayer,
  normalizeFeaturesForMap,
  requiresLargeLayerOptimizationRecreation,
  updateVectorLayer,
} from '../utils/geojsonToVectorLayer';
import { useAttributeMapSync } from '../hooks/useAttributeMapSync';
import { useSplitLineDrawing } from '../hooks/useSplitLineDrawing';
import { useMapNavigation } from '../hooks/useMapNavigation';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import { useDrawingInteraction } from '../hooks/useDrawingInteraction';
import { useMeasurementInteraction } from '../hooks/useMeasurementInteraction';
import { useNodeEditingInteractions } from '../hooks/useNodeEditingInteractions';
import { useNodeEditingOverlay } from '../hooks/useNodeEditingOverlay';
import { useNodeDragInteraction } from '../hooks/useNodeDragInteraction';
import { useNodeAddInteraction } from '../hooks/useNodeAddInteraction';
import ZoomControls from './ZoomControls';
import { layerCreatorService } from '../services/LayerCreatorService';
import { mapNavigator } from '../services/MapNavigator';
import 'ol/ol.css';
import './Map.css';

interface MapComponentProps {
  center?: [number, number]; // [longitude, latitude]
  zoom?: number;
  className?: string;
  useReduxState?: boolean; // Whether to sync with Redux state
  basemapType?: BasemapType; // Basemap type to use (overrides Redux state if provided)
}

// Error handling configuration
const ERROR_HANDLING_CONFIG = {
  MAX_RETRY_ATTEMPTS: 3,
  INITIAL_RETRY_DELAY: 1000, // 1 second
  MAX_RETRY_DELAY: 8000, // 8 seconds
  FAILED_TILE_THRESHOLD: 5, // Number of failed tiles before triggering fallback
  ERROR_RESET_DELAY: 3000, // Time to wait before clearing error after successful load
  FALLBACK_BASEMAP: 'osm' as BasemapType, // Fallback to OpenStreetMap
};

/**
 * OpenLayers Map Component
 *
 * A React wrapper for OpenLayers map with support for multiple basemap providers and vector layer rendering.
 * Supports customizable center coordinates, zoom level, basemap selection, and dynamic vector layer management.
 * Can optionally sync with Redux state for centralized map state management.
 *
 * Features:
 * - Multiple basemap providers (OpenStreetMap, satellite, terrain)
 * - Dynamic basemap switching without losing map position
 * - Vector layer rendering with visibility toggle support
 * - Layer z-order management and opacity control
 * - Error handling for basemap loading failures
 * - Redux state integration for centralized state management
 *
 * Layer Ordering Architecture:
 * - Basemap layer is always rendered at zIndex: 0 (bottom layer)
 * - Vector layers are rendered with zIndex > 0 to appear above the basemap
 * - Layer z-order is determined by the layer.zIndex property from Redux state
 * - When basemap is switched, only the basemap layer source is updated via setSource()
 * - This approach preserves all other map layers and their properties:
 *   - Layer visibility state
 *   - Layer z-order (stacking order)
 *   - Layer opacity
 *   - Layer symbology/styling
 *   - Map view (center, zoom, rotation)
 *
 * Vector Layer Management:
 * - Vector layers are created from Redux layers state
 * - Each layer is rendered as an OpenLayers VectorLayer with GeoJSON source
 * - Layer visibility is controlled by the visible property
 * - Layer opacity is controlled by the opacity property
 * - Layer symbology is applied from the symbology configuration
 * - Layers are automatically updated when Redux state changes
 *
 * Basemap Switching Implementation:
 * - The basemap layer reference is stored in basemapLayerRef.current
 * - When basemapType changes, a new source is created and applied via setSource()
 * - This method updates only the tile source without recreating the layer
 * - All other layers remain untouched, maintaining their state and position
 *
 * **Validates: Requirements 4.3, 4.4, 5.5, 18.5** - Layer visibility toggle,
 * layer display order, basemap switching without losing layer state, layer persistence
 */
const MapComponent: React.FC<MapComponentProps> = ({
  center: propCenter,
  zoom: propZoom,
  className = '',
  useReduxState = false,
  basemapType: propBasemapType,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  /** Stable map handle so navigation/draw/measure hooks re-bind after init */
  const [olMap, setOlMap] = useState<Map | null>(null);
  const basemapLayerRef = useRef<TileLayer<TileSource> | null>(null);
  const vectorLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(
    new globalThis.Map()
  );
  const rasterLayersRef = useRef<globalThis.Map<string, WebGLTileLayer | TileLayer<TileSource>>>(
    new globalThis.Map()
  );
  const hoveredFeatureRef = useRef<Feature<Geometry> | null>(null);
  const originalStyleRef = useRef<Style | Style[] | null>(null);
  const dragBoxInteractionRef = useRef<DragBox | null>(null);
  const activeDigitizationSessionRef = useRef<string | null>(null);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const reduxMapView = useAppSelector((state) => state.map.view);
  const reduxBasemapType = useAppSelector((state) => state.map.currentBasemap);
  const layers = useAppSelector((state) => state.layers.layers);
  const activeLayerId = useAppSelector((state) => state.layers.activeLayerId);
  const selectedFeatures = useAppSelector(selectSelectedFeatures);
  const snappingConfig = useAppSelector((state) => state.digitization.snapping);
  const digitizationMode = useAppSelector((state) => state.digitization.mode);
  const activeDigitizationLayerId = useAppSelector(
    (state) => state.digitization.activeDigitizationLayerId
  );
  const activeDigitizationGeometryType = useAppSelector(
    (state) => state.digitization.activeGeometryType
  );

  // Track features that currently have selection highlight applied
  // Maps "layerId::featureId" -> original style (or null if using layer default)
  const selectionStylesRef = useRef<globalThis.Map<string, Style | Style[] | null>>(
    new globalThis.Map()
  );
  const selectedFeaturesRef = useRef(selectedFeatures);
  selectedFeaturesRef.current = selectedFeatures;
  const snappingConfigRef = useRef(snappingConfig);
  snappingConfigRef.current = snappingConfig;

  // Real-time attribute-to-map synchronization
  useAttributeMapSync({ vectorLayersRef });

  // Split / draw / measure / node-edit — must run on live map instance
  useSplitLineDrawing({ map: olMap, mapRef: mapInstanceRef });
  useMapNavigation(olMap);
  useDrawingInteraction(olMap);
  useMeasurementInteraction({ map: olMap, mapRef: mapInstanceRef });
  useNodeEditingInteractions();
  useNodeEditingOverlay({ map: olMap, mapRef: mapInstanceRef });
  useNodeDragInteraction({ map: olMap, mapRef: mapInstanceRef });
  useNodeAddInteraction({ map: olMap, mapRef: mapInstanceRef });

  // ── Digitization and snapping service ownership ──────────────────────────
  useEffect(() => {
    if (!olMap) return;

    layerCreatorService.setOnDrawEnd((geometry) => {
      const validation = layerCreatorService.validateGeometryTopology(geometry);
      if (!validation.valid) {
        dispatch(setDigitizationError(`Invalid geometry: ${validation.errors.join(', ')}`));
        return;
      }
      dispatch(setPendingGeometry(geometry));
    });

    return () => {
      layerCreatorService.setOnDrawEnd(null);
    };
  }, [olMap, dispatch]);

  const digitizationLayerExists =
    activeDigitizationLayerId !== null &&
    layers.some((layer) => layer.id === activeDigitizationLayerId && layer.type !== 'raster');

  useEffect(() => {
    if (!olMap) return;

    if (
      digitizationMode === 'drawing' &&
      activeDigitizationGeometryType &&
      digitizationLayerExists
    ) {
      const sessionKey = `${activeDigitizationLayerId}::${activeDigitizationGeometryType}`;
      if (activeDigitizationSessionRef.current !== sessionKey) {
        layerCreatorService.activateDigitization(activeDigitizationGeometryType);
        activeDigitizationSessionRef.current = sessionKey;
      }
      return;
    }

    layerCreatorService.deactivateDigitization();
    activeDigitizationSessionRef.current = null;
    if (digitizationMode !== 'idle' && activeDigitizationLayerId && !digitizationLayerExists) {
      dispatch(setDigitizationError('The layer being digitized is no longer available'));
      dispatch(stopDigitization());
    }
  }, [
    olMap,
    digitizationMode,
    activeDigitizationGeometryType,
    activeDigitizationLayerId,
    digitizationLayerExists,
    dispatch,
  ]);

  useEffect(() => {
    if (olMap) {
      layerCreatorService.enableSnapping(snappingConfig);
    }
  }, [olMap, snappingConfig]);

  // Keyboard arrow key panning
  useKeyboardNavigation();

  // Error handling state
  const retryCountRef = useRef<number>(0);
  const failedTileCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const errorResetTimeoutRef = useRef<number | null>(null);
  const previousBasemapRef = useRef<BasemapType | null>(null);
  const hasTriggeredFallbackRef = useRef<boolean>(false);

  // Determine which center, zoom, and basemap to use
  const center = useReduxState ? reduxMapView.center : (propCenter ?? [0, 0]);
  const zoom = useReduxState ? reduxMapView.zoom : (propZoom ?? 2);
  const rotation = useReduxState ? reduxMapView.rotation : 0;
  const basemapType = propBasemapType ?? (useReduxState ? reduxBasemapType : 'osm');

  /**
   * Create OpenLayers VectorLayer from Redux layer data using the converter utility
   */
  const createVectorLayer = useCallback((layer: Layer): VectorLayer<VectorSource> => {
    try {
      return geojsonToVectorLayer({ layer });
    } catch (err) {
      console.warn('[Map] Failed to create vector layer, using empty source', layer.id, err);
      // Never crash the whole dashboard — empty layer keeps UI usable
      return geojsonToVectorLayer({
        layer: { ...layer, features: [] },
      });
    }
  }, []);

  /**
   * Create an OpenLayers raster layer (COG / XYZ / WMS).
   * Uses WebGLTile + GeoTIFF source for high-performance COG rendering when possible.
   */
  const createRasterLayerOL = useCallback(
    (layer: Layer): WebGLTileLayer | TileLayer<TileSource> => {
      const { sourceType = 'cog', sourceUrl, renderStyle = {}, opacity = 0.85 } = layer as any;

      let olLayer: WebGLTileLayer | TileLayer<TileSource>;

      if (sourceType === 'cog' || sourceType === 'geotiff') {
        // Cloud Optimized GeoTIFF - best quality path
        const source = new OLGeoTIFF({
          sources: [
            {
              url: sourceUrl,
              bands: (renderStyle as any)?.bands || [1],
              nodata: (renderStyle as any)?.nodata ?? null,
            },
          ],
          convertToRGB: (renderStyle as any)?.convertToRGB ?? true,
        });

        olLayer = new WebGLTileLayer({
          source,
          opacity: typeof opacity === 'number' ? opacity : 0.85,
          zIndex: layer.zIndex ?? 10,
          properties: { layerId: layer.id, layerType: 'raster' },
        });
      } else if (sourceType === 'wms') {
        // WMS via TileWMS
        const source = new TileWMS({
          url: sourceUrl,
          params: {
            LAYERS: (layer as any).wmsLayers || '0',
            TILED: true,
            ...((layer as any).wmsParams || {}),
          },
          serverType: 'geoserver',
          crossOrigin: 'anonymous',
        });
        olLayer = new TileLayer({
          source,
          opacity: typeof opacity === 'number' ? opacity : 0.85,
          zIndex: layer.zIndex ?? 10,
          properties: { layerId: layer.id, layerType: 'raster' },
        });
      } else {
        // Default: XYZ / TMS tiles (including many raster tile services)
        const source = new XYZ({
          url: sourceUrl,
          crossOrigin: 'anonymous',
          maxZoom: (layer as any).maxZoom ?? 22,
        });
        olLayer = new TileLayer({
          source,
          opacity: typeof opacity === 'number' ? opacity : 0.85,
          zIndex: layer.zIndex ?? 10,
          properties: { layerId: layer.id, layerType: 'raster' },
        });
      }

      // Apply visibility
      olLayer.setVisible(layer.visible !== false);
      return olLayer;
    },
    []
  );

  /**
   * Update existing vector layer with new data using the converter utility
   */
  const updateVectorLayerCallback = useCallback(
    (vectorLayer: VectorLayer<VectorSource>, layer: Layer): void => {
      try {
        updateVectorLayer(vectorLayer, { layer });
      } catch (err) {
        console.warn('[Map] Failed to update vector layer', layer.id, err);
      }
    },
    []
  );

  /**
   * Create hover highlight style for a feature
   * Increases stroke width and changes stroke color to highlight the feature
   */
  const createHoverStyle = useCallback(
    (feature: Feature<Geometry>, geometryType: string): Style => {
      const geometry = feature.getGeometry();
      if (!geometry) {
        return new Style({});
      }

      // Create highlight style based on geometry type
      const baseConfig: any = {
        stroke: new Stroke({
          color: '#ffff00', // Yellow highlight
          width: 4, // Increased width for visibility
        }),
      };

      // Add fill for Polygon features
      if (geometryType === 'Polygon') {
        baseConfig.fill = new Fill({
          color: 'rgba(255, 255, 0, 0.3)', // Semi-transparent yellow
        });
      }

      // Add image style for Point features
      if (geometryType === 'Point') {
        baseConfig.image = new Circle({
          radius: 10, // Slightly larger than default
          fill: new Fill({
            color: 'rgba(255, 255, 0, 0.6)', // Semi-transparent yellow
          }),
          stroke: new Stroke({
            color: '#ffff00', // Yellow border
            width: 3,
          }),
        });
      }

      return new Style(baseConfig);
    },
    []
  );

  /**
   * Create selection highlight style for a feature.
   * Uses a distinct cyan/blue color to differentiate from the yellow hover style.
   *
   * AC 1 (Req 11): WHEN a Registered_User clicks on a Feature, THE Feature_Selector
   *                SHALL select the feature and highlight it on the map.
   */
  const createSelectionStyle = useCallback((geometryType: string): Style => {
    const baseConfig: any = {
      stroke: new Stroke({
        color: '#00bcd4', // Cyan selection highlight
        width: 3,
      }),
    };

    if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
      baseConfig.fill = new Fill({
        color: 'rgba(0, 188, 212, 0.25)', // Semi-transparent cyan
      });
    }

    if (geometryType === 'Point' || geometryType === 'MultiPoint') {
      baseConfig.image = new Circle({
        radius: 9,
        fill: new Fill({
          color: 'rgba(0, 188, 212, 0.5)',
        }),
        stroke: new Stroke({
          color: '#00bcd4',
          width: 3,
        }),
      });
    }

    return new Style(baseConfig);
  }, []);

  /**
   * Calculate exponential backoff delay for retry attempts
   */
  const calculateRetryDelay = useCallback((attemptNumber: number): number => {
    const delay = ERROR_HANDLING_CONFIG.INITIAL_RETRY_DELAY * Math.pow(2, attemptNumber);
    return Math.min(delay, ERROR_HANDLING_CONFIG.MAX_RETRY_DELAY);
  }, []);

  /**
   * Reset error handling state
   */
  const resetErrorState = useCallback(() => {
    retryCountRef.current = 0;
    failedTileCountRef.current = 0;
    hasTriggeredFallbackRef.current = false;

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (errorResetTimeoutRef.current) {
      clearTimeout(errorResetTimeoutRef.current);
      errorResetTimeoutRef.current = null;
    }
  }, []);

  /**
   * Handle successful tile load - clear errors after a delay
   */
  const handleTileLoadSuccess = useCallback(() => {
    // Clear any pending error reset timeout
    if (errorResetTimeoutRef.current) {
      clearTimeout(errorResetTimeoutRef.current);
    }

    // Reset error state after successful loads
    errorResetTimeoutRef.current = setTimeout(() => {
      if (failedTileCountRef.current > 0) {
        // Reset counters if we've had successful loads
        failedTileCountRef.current = 0;
        retryCountRef.current = 0;

        // Clear error message if present
        dispatch(setBasemapError(null));
      }
    }, ERROR_HANDLING_CONFIG.ERROR_RESET_DELAY);
  }, [dispatch]);

  /**
   * Trigger fallback to default basemap
   */
  const triggerFallback = useCallback(() => {
    if (hasTriggeredFallbackRef.current) {
      return; // Already triggered fallback
    }

    if (basemapType === ERROR_HANDLING_CONFIG.FALLBACK_BASEMAP) {
      // Already on fallback basemap, can't fallback further
      const errorMessage = `Unable to load ${getBasemapConfig(basemapType).name} tiles. Please check your internet connection.`;
      dispatch(setBasemapError(errorMessage));
      return;
    }

    hasTriggeredFallbackRef.current = true;
    const currentBasemapName = getBasemapConfig(basemapType).name;
    const fallbackBasemapName = getBasemapConfig(ERROR_HANDLING_CONFIG.FALLBACK_BASEMAP).name;

    const errorMessage = `Failed to load ${currentBasemapName} after multiple attempts. Switching to ${fallbackBasemapName} as fallback.`;
    dispatch(setBasemapError(errorMessage));

    // Switch to fallback basemap
    dispatch(setCurrentBasemap(ERROR_HANDLING_CONFIG.FALLBACK_BASEMAP));
  }, [basemapType, dispatch]);

  /**
   * Handle tile load error with retry logic
   */
  const handleTileLoadError = useCallback(() => {
    failedTileCountRef.current += 1;

    // Check if we've exceeded the failed tile threshold
    if (failedTileCountRef.current >= ERROR_HANDLING_CONFIG.FAILED_TILE_THRESHOLD) {
      if (retryCountRef.current < ERROR_HANDLING_CONFIG.MAX_RETRY_ATTEMPTS) {
        // Attempt retry with exponential backoff
        const retryDelay = calculateRetryDelay(retryCountRef.current);
        retryCountRef.current += 1;

        const basemapConfig = getBasemapConfig(basemapType);
        const errorMessage = `Failed to load tiles from ${basemapConfig.name}. Retrying in ${Math.round(retryDelay / 1000)} seconds... (Attempt ${retryCountRef.current}/${ERROR_HANDLING_CONFIG.MAX_RETRY_ATTEMPTS})`;
        dispatch(setBasemapError(errorMessage));

        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        // Schedule retry
        retryTimeoutRef.current = setTimeout(() => {
          // Reset failed tile count for retry attempt
          failedTileCountRef.current = 0;

          // Force refresh by recreating the source
          if (basemapLayerRef.current) {
            const newSource = basemapConfig.createSource();

            // Set up error and success handlers for new source
            newSource.on('tileloaderror', handleTileLoadError);
            newSource.on('tileloadend', handleTileLoadSuccess);

            basemapLayerRef.current.setSource(newSource);
          }
        }, retryDelay);
      } else {
        // Max retries exceeded, trigger fallback
        triggerFallback();
      }
    } else {
      // Haven't reached threshold yet, just show a warning
      const basemapConfig = getBasemapConfig(basemapType);
      const errorMessage = `Some tiles from ${basemapConfig.name} failed to load. Please check your internet connection.`;
      dispatch(setBasemapError(errorMessage));
    }
  }, [basemapType, calculateRetryDelay, dispatch, handleTileLoadSuccess, triggerFallback]);

  // Initialize map once on mount
  useEffect(() => {
    if (!mapRef.current) return;

    // Get basemap configuration
    const basemapConfig = getBasemapConfig(basemapType);

    // Create basemap layer
    const basemapLayer = new TileLayer({
      source: basemapConfig.createSource() as TileSource,
      zIndex: 0, // CRITICAL: Basemap must always be at zIndex 0 (bottom layer)
      // Vector layers will be added with zIndex > 0 to appear on top
    });
    basemapLayerRef.current = basemapLayer;
    previousBasemapRef.current = basemapType;

    // Set up error handling for basemap tiles
    const source = basemapLayer.getSource();
    if (source) {
      // Use any type to bypass TypeScript event type checking for tile events
      (source as any).on('tileloaderror', handleTileLoadError);
      (source as any).on('tileloadend', handleTileLoadSuccess);
    }

    // Initialize OpenLayers map
    const map = new Map({
      target: mapRef.current,
      layers: [basemapLayer],
      view: new View({
        center: fromLonLat(center), // Convert from lon/lat to Web Mercator
        zoom: zoom,
        rotation,
        projection: 'EPSG:3857', // Web Mercator projection
      }),
    });

    mapInstanceRef.current = map;
    setOlMap(map);
    layerCreatorService.initialize(map);
    mapNavigator.setMap(map);

    // Status bar: live cursor coordinates + DIGIPIN + scale
    const onPointerMove = (evt: any) => {
      if (!evt?.coordinate) return;
      const [lon, lat] = toLonLat(evt.coordinate);
      const digipin = encodeDigipin(lat, lon);
      window.dispatchEvent(
        new CustomEvent('map:cursorcoords', {
          detail: {
            lat,
            lon,
            digipin,
            formatted: `${lat.toFixed(5)}°, ${lon.toFixed(5)}°`,
          },
        })
      );
    };
    const onMoveEnd = () => {
      const view = map.getView();
      const res = view.getResolution();
      const zoom = view.getZoom();
      if (res != null && zoom != null) {
        // rough scale for status bar (Web Mercator at equator)
        const scale = Math.round(res * 39.37 * 96);
        window.dispatchEvent(new CustomEvent('map:scale', { detail: `1:${scale}` }));
      }
    };
    map.on('pointermove', onPointerMove);
    map.on('moveend', onMoveEnd);
    onMoveEnd();

    // Force map to update its size after initialization
    setTimeout(() => {
      map.updateSize();
    }, 100);

    // Selection must yield to interactions that own map clicks or drags.
    // Read Redux state at event time because these handlers are registered once.
    const isExclusiveMapInteractionActive = (): boolean => {
      const state = store.getState();
      return (
        layerCreatorService.isDigitizing() ||
        state.digitization.mode !== 'idle' ||
        state.drawing.activeTool !== null ||
        state.measurement.activeMode !== null ||
        state.geometryEditing.isSplitToolActive ||
        state.geometryEditing.isEditingGeometry
      );
    };

    // Set up click-to-select feature interaction
    // AC 1: WHEN a Registered_User clicks on a Feature, THE Feature_Selector
    //       SHALL select the feature and highlight it on the map.
    // AC 4: WHEN a Registered_User clicks on empty space, THE Feature_Selector
    //       SHALL clear the current selection.
    map.on('singleclick', (event) => {
      if (isExclusiveMapInteractionActive()) return;

      const pixel = event.pixel;

      // Collect the topmost feature and its owning layer
      let hitFeature: Feature<Geometry> | null = null;
      let hitLayerId: string | null = null;

      map.forEachFeatureAtPixel(
        pixel,
        (feat, layer) => {
          // Only consider vector layers with a layerId (skip basemap, drawing overlays, etc.)
          if (layer && (layer as any).layerId) {
            hitFeature = feat as Feature<Geometry>;
            hitLayerId = (layer as any).layerId;
          }
          return true; // stop after first hit (topmost feature)
        },
        { hitTolerance: 5 }
      );

      if (hitFeature && hitLayerId) {
        // Determine the feature ID — OpenLayers stores it via getId() or as a property
        const featureId =
          (hitFeature as Feature<Geometry>).getId()?.toString() ??
          (hitFeature as Feature<Geometry>).get('id')?.toString() ??
          '';

        if (featureId) {
          if (event.originalEvent.shiftKey) {
            // Shift-click: toggle feature in selection (AC 3)
            // If the feature is already selected, remove it; otherwise add it.
            const currentSelection = selectSelectedFeatures(store.getState());
            const alreadySelected = currentSelection.some(
              (f) => f.featureId === featureId && f.layerId === hitLayerId
            );
            if (alreadySelected) {
              dispatch(removeFromSelection({ featureId, layerId: hitLayerId }));
            } else {
              dispatch(
                addFeatureToSelection({
                  featureId,
                  layerId: hitLayerId,
                  properties: (hitFeature as Feature<Geometry>).getProperties(),
                })
              );
            }
          } else {
            // Normal click: replace selection (AC 1)
            dispatch(
              selectFeature({
                featureId,
                layerId: hitLayerId,
                properties: (hitFeature as Feature<Geometry>).getProperties(),
              })
            );
          }
        }
      } else {
        // Clicked on empty space — clear selection (AC 4)
        dispatch(clearSelection());
      }
    });

    // Set up box selection (drag rectangle) interaction
    // AC 2: WHEN a Registered_User draws a selection box, THE Feature_Selector
    //       SHALL select all features within the box.
    // The DragBox is activated by holding the platform modifier key (Ctrl on
    // Windows/Linux, Cmd on macOS) and dragging on the map.
    const dragBox = new DragBox({
      condition: (event) => platformModifierKeyOnly(event) && !isExclusiveMapInteractionActive(),
    });

    dragBox.on('boxend', () => {
      if (isExclusiveMapInteractionActive()) return;

      const boxExtent = dragBox.getGeometry().getExtent();

      // Collect all features within the box extent from all visible vector layers
      const selectedFeatures: Array<{
        featureId: string;
        layerId: string;
        properties: Record<string, unknown>;
      }> = [];

      vectorLayersRef.current.forEach((vectorLayer, layerId) => {
        // Only select from visible layers
        if (!vectorLayer.getVisible()) return;

        const source = vectorLayer.getSource();
        if (!source) return;

        // Get features that intersect with the box extent
        source.forEachFeatureIntersectingExtent(boxExtent, (feature) => {
          const featureId = feature.getId()?.toString() ?? feature.get('id')?.toString() ?? '';

          if (featureId) {
            selectedFeatures.push({
              featureId,
              layerId,
              properties: feature.getProperties(),
            });
          }
        });
      });

      // Replace the current selection with the box-selected features
      dispatch(setSelection(selectedFeatures));
    });

    // Clear selection when box drag starts (user is making a new box selection)
    dragBox.on('boxstart', () => {
      if (isExclusiveMapInteractionActive()) return;
      dispatch(clearSelection());
    });

    map.addInteraction(dragBox);
    dragBoxInteractionRef.current = dragBox;

    // Set up hover interaction for feature highlighting
    map.on('pointermove', (event) => {
      const pixel = event.pixel;

      // Check if there's a feature at the cursor position
      const feature = map.forEachFeatureAtPixel(pixel, (feat) => feat as Feature<Geometry>, {
        hitTolerance: 5, // 5 pixel tolerance for easier selection
      });

      // Update cursor style
      const target = map.getTarget();
      if (target && typeof target !== 'string') {
        const element = target as HTMLElement;
        element.style.cursor = feature ? 'pointer' : '';
      }

      // Handle feature highlighting
      if (feature !== hoveredFeatureRef.current) {
        // Remove highlight from previously hovered feature (only if it's not selected)
        if (hoveredFeatureRef.current && originalStyleRef.current !== undefined) {
          // Check if the previously hovered feature is currently selected
          const prevFeatureId =
            hoveredFeatureRef.current.getId()?.toString() ??
            hoveredFeatureRef.current.get('id')?.toString() ??
            '';
          let prevLayerId = '';
          mapInstanceRef.current?.getLayers().forEach((layer) => {
            if (layer instanceof VectorLayer) {
              const src = layer.getSource();
              if (src && src.hasFeature(hoveredFeatureRef.current!)) {
                prevLayerId = (layer as any).layerId || '';
              }
            }
          });
          const prevKey = `${prevLayerId}::${prevFeatureId}`;
          const isSelected = selectionStylesRef.current.has(prevKey);

          if (isSelected) {
            // Restore selection style instead of original style
            const geometryType = (() => {
              const vl = vectorLayersRef.current.get(prevLayerId);
              return (vl as any)?.geometryType || 'Point';
            })();
            hoveredFeatureRef.current.setStyle(createSelectionStyle(geometryType));
          } else {
            hoveredFeatureRef.current.setStyle(originalStyleRef.current);
          }
          hoveredFeatureRef.current = null;
          originalStyleRef.current = null;
        }

        // Add highlight to newly hovered feature
        if (feature) {
          // Store original style (could be selection style or layer default)
          originalStyleRef.current = feature.getStyle() as Style | Style[] | null;

          // Determine geometry type from the layer
          let geometryType = 'Point'; // Default
          map.getLayers().forEach((layer) => {
            if (layer instanceof VectorLayer) {
              const source = layer.getSource();
              if (source && source.hasFeature(feature)) {
                geometryType = (layer as any).geometryType || 'Point';
              }
            }
          });

          // Apply hover style
          const hoverStyle = createHoverStyle(feature, geometryType);
          feature.setStyle(hoverStyle);
          hoveredFeatureRef.current = feature;
        }
      }
    });

    // If using Redux state, sync map changes back to Redux
    if (useReduxState) {
      const view = map.getView();

      // Listen for view changes (moveend fires after pan/zoom)
      map.on('moveend', () => {
        const newCenter = view.getCenter();
        const newZoom = view.getZoom();

        if (newCenter && newZoom !== undefined) {
          // Convert center back to lon/lat for Redux state
          const lonLat = toLonLat(newCenter) as [number, number];
          dispatch(
            setMapView({
              center: lonLat,
              zoom: newZoom,
              rotation: view.getRotation(),
            })
          );

          // Update extent in Redux
          const extent = view.calculateExtent(map.getSize());
          dispatch(
            setMapExtent({
              minX: extent[0],
              minY: extent[1],
              maxX: extent[2],
              maxY: extent[3],
            })
          );
        }
      });
    }

    // Update map size when window resizes
    const handleResize = () => {
      map.updateSize();
    };
    window.addEventListener('resize', handleResize);

    // Listen for zoom-to-extent requests from LayerListPanel
    // Extent arrives as WGS84 [minX, minY, maxX, maxY]; convert to EPSG:3857
    const handleZoomToExtent = (e: Event) => {
      const [minLon, minLat, maxLon, maxLat] = (e as CustomEvent<number[]>).detail;
      const bottomLeft = fromLonLat([minLon, minLat]);
      const topRight = fromLonLat([maxLon, maxLat]);
      const extent3857 = [bottomLeft[0], bottomLeft[1], topRight[0], topRight[1]];
      map.getView().fit(extent3857, {
        size: map.getSize(),
        padding: [40, 40, 40, 40],
        duration: 500,
        maxZoom: 18,
      });
    };
    window.addEventListener('map:zoomtoextent', handleZoomToExtent);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('map:zoomtoextent', handleZoomToExtent);
      resetErrorState();

      layerCreatorService.dispose();
      activeDigitizationSessionRef.current = null;

      // Clear hover state
      if (hoveredFeatureRef.current && originalStyleRef.current) {
        hoveredFeatureRef.current.setStyle(originalStyleRef.current);
      }
      hoveredFeatureRef.current = null;
      originalStyleRef.current = null;

      // Clear selection highlight state
      selectionStylesRef.current.clear();

      // Clean up DragBox interaction
      dragBoxInteractionRef.current = null;

      vectorLayersRef.current.clear();
      map.un('pointermove', onPointerMove);
      map.un('moveend', onMoveEnd);
      map.setTarget(undefined);
      mapInstanceRef.current = null;
      setOlMap(null);
      mapNavigator.setMap(null);
      basemapLayerRef.current = null;
      previousBasemapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - initialize once

  // Update view when center, zoom, or rotation changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      const view = mapInstanceRef.current.getView();
      view.setCenter(fromLonLat(center));
      view.setZoom(zoom);
      view.setRotation(rotation);
    }
  }, [center, zoom, rotation]);

  // Update basemap when basemapType changes
  useEffect(() => {
    if (mapInstanceRef.current && basemapLayerRef.current) {
      // Check if basemap actually changed
      if (previousBasemapRef.current === basemapType) {
        return;
      }

      try {
        // Store previous basemap for potential rollback
        previousBasemapRef.current = basemapType;

        // Reset error handling state for new basemap
        resetErrorState();

        // Get new basemap configuration
        const basemapConfig = getBasemapConfig(basemapType);

        // Create new source
        const newSource = basemapConfig.createSource() as TileSource;

        // Set up error handling for new source
        (newSource as any).on('tileloaderror', handleTileLoadError);
        (newSource as any).on('tileloadend', handleTileLoadSuccess);

        // CRITICAL: Update only the basemap layer source, not the entire layer
        // This preserves all other layers on the map (vector layers, overlays, etc.)
        // By using setSource() instead of recreating the layer:
        // - Vector layers remain visible with their original z-index
        // - Layer visibility, opacity, and symbology are preserved
        // - Map view (center, zoom, rotation) is not affected
        basemapLayerRef.current.setSource(newSource);

        // Clear any previous errors on successful basemap change
        dispatch(setBasemapError(null));
      } catch (error) {
        const errorMessage = `Failed to switch to basemap: ${error instanceof Error ? error.message : 'Unknown error'}`;
        dispatch(setBasemapError(errorMessage));

        // Revert to previous basemap if available
        if (previousBasemapRef.current && previousBasemapRef.current !== basemapType) {
          previousBasemapRef.current = basemapType; // Prevent infinite loop
        }
      }
    }
  }, [basemapType, dispatch, handleTileLoadError, handleTileLoadSuccess, resetErrorState]);

  const clearLayerInteractionState = useCallback(
    (layerId: string, vectorLayer: VectorLayer<VectorSource>) => {
      const source = vectorLayer.getSource();
      if (source && hoveredFeatureRef.current && source.hasFeature(hoveredFeatureRef.current)) {
        hoveredFeatureRef.current.setStyle(originalStyleRef.current || undefined);
        hoveredFeatureRef.current = null;
        originalStyleRef.current = null;
      }

      const prefix = `${layerId}::`;
      for (const key of selectionStylesRef.current.keys()) {
        if (key.startsWith(prefix)) selectionStylesRef.current.delete(key);
      }
    },
    []
  );

  const applySelectionStyles = useCallback(() => {
    const currentSelectionKeys = new Set(
      selectedFeaturesRef.current.map((sel) => `${sel.layerId}::${sel.featureId}`)
    );

    for (const [key] of selectionStylesRef.current) {
      if (currentSelectionKeys.has(key)) continue;
      const [layerId, featureId] = key.split('::');
      const source = vectorLayersRef.current.get(layerId)?.getSource();
      const feature = source
        ?.getFeatures()
        .find(
          (item) => (item.getId()?.toString() ?? item.get('id')?.toString() ?? '') === featureId
        );
      feature?.setStyle(undefined as any);
      selectionStylesRef.current.delete(key);
    }

    for (const sel of selectedFeaturesRef.current) {
      const key = `${sel.layerId}::${sel.featureId}`;
      if (selectionStylesRef.current.has(key)) continue;

      const vectorLayer = vectorLayersRef.current.get(sel.layerId);
      const feature = vectorLayer
        ?.getSource()
        ?.getFeatures()
        .find(
          (item) => (item.getId()?.toString() ?? item.get('id')?.toString() ?? '') === sel.featureId
        );
      if (!vectorLayer || !feature) continue;

      selectionStylesRef.current.set(key, feature.getStyle() as Style | Style[] | null);
      feature.setStyle(createSelectionStyle((vectorLayer as any).geometryType || 'Point'));
    }
  }, [createSelectionStyle]);

  // Manage vector layers when layers state changes
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const currentVectorLayers = vectorLayersRef.current;
    const vectorLayers = layers.filter((layer) => layer.type !== 'raster');
    const currentLayerIds = new Set(vectorLayers.map((layer) => layer.id));

    for (const [layerId, vectorLayer] of currentVectorLayers.entries()) {
      if (!currentLayerIds.has(layerId)) {
        clearLayerInteractionState(layerId, vectorLayer);
        map.removeLayer(vectorLayer);
        currentVectorLayers.delete(layerId);
      }
    }

    for (const layer of vectorLayers) {
      const existingVectorLayer = currentVectorLayers.get(layer.id);
      if (!existingVectorLayer) {
        const newVectorLayer = createVectorLayer(layer);
        map.addLayer(newVectorLayer);
        currentVectorLayers.set(layer.id, newVectorLayer);
        continue;
      }

      clearLayerInteractionState(layer.id, existingVectorLayer);
      const currentFeatureCount = Number(
        (existingVectorLayer as any).featureCount ??
          existingVectorLayer.getSource()?.getFeatures().length ??
          0
      );
      const nextFeatureCount = normalizeFeaturesForMap(layer.features || []).length;

      if (requiresLargeLayerOptimizationRecreation(currentFeatureCount, nextFeatureCount)) {
        try {
          const replacement = geojsonToVectorLayer({ layer }) as VectorLayer<VectorSource>;
          const collection = map.getLayers();
          const oldIndex = collection.getArray().indexOf(existingVectorLayer);
          map.removeLayer(existingVectorLayer);
          if (oldIndex >= 0) {
            collection.insertAt(oldIndex, replacement);
          } else {
            map.addLayer(replacement);
          }
          currentVectorLayers.set(layer.id, replacement);
        } catch (error) {
          console.warn(
            '[Map] Failed to recreate vector layer; retaining existing layer',
            layer.id,
            error
          );
          updateVectorLayerCallback(existingVectorLayer, layer);
        }
      } else {
        updateVectorLayerCallback(existingVectorLayer, layer);
      }
    }

    applySelectionStyles();
    layerCreatorService.enableSnapping(snappingConfigRef.current);
  }, [
    layers,
    createVectorLayer,
    updateVectorLayerCallback,
    clearLayerInteractionState,
    applySelectionStyles,
  ]);

  // Manage raster layers (COG / XYZ / WMS)
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const currentRasters = rasterLayersRef.current;

    const rasterLayersInState = layers.filter((l) => l.type === 'raster');
    const currentRasterIds = new Set(rasterLayersInState.map((l) => l.id));

    // Remove deleted rasters
    for (const [id, rl] of currentRasters.entries()) {
      if (!currentRasterIds.has(id)) {
        map.removeLayer(rl);
        currentRasters.delete(id);
      }
    }

    // Add / update rasters
    for (const layer of rasterLayersInState) {
      const existing = currentRasters.get(layer.id);
      if (existing) {
        // Update common properties
        existing.setVisible(layer.visible !== false);
        if (typeof (layer as any).opacity === 'number') {
          existing.setOpacity((layer as any).opacity);
        }
        // For full style changes (colormap etc) we would recreate; keep simple for now
      } else {
        const newRaster = createRasterLayerOL(layer);
        // Ensure zIndex is high enough to sit above vectors if desired, but below UI
        newRaster.setZIndex(layer.zIndex ?? 5);
        map.addLayer(newRaster);
        currentRasters.set(layer.id, newRaster);
      }
    }
  }, [layers, createRasterLayerOL]);

  // ── Selection highlighting ────────────────────────────────────────────────
  // When the Redux selection state changes, apply or remove visual highlight
  // styles on the corresponding OpenLayers features.
  //
  // AC 1 (Req 11): WHEN a Registered_User clicks on a Feature, THE Feature_Selector
  //                SHALL select the feature and highlight it on the map.
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    applySelectionStyles();
  }, [selectedFeatures, applySelectionStyles]);

  return (
    <div
      ref={mapRef}
      className={`map-container ${className}`}
      data-testid="map-container"
      style={{ position: 'relative' }}
    >
      <ZoomControls />
    </div>
  );
};

export default MapComponent;
